import * as z from 'zod';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '@noor/config';

/**
 * URL search-param schema for the migrated `/clients/all` view (the second
 * `/merchants` tab). Every key is `c`-prefixed so the legacy view never
 * collides with the merchants list params (`search_type`, `status`, `page`…)
 * that share the same route URL — each tab keeps its own state while the
 * other tab is on screen, and old `/merchants` bookmarks keep validating.
 *
 * Coerced + caught (never strict): the router JSON-parses search params, so
 * a bare numeric link (`?ctariff=12`) arrives as a number, and a hand-made
 * or stale value must fall back to the default instead of crashing route
 * validation.
 */

export const merchantsTabSchema = z
  .enum(['merchants', 'clients', 'employees'])
  .catch('merchants')
  .default('merchants');
export type MerchantsTab = z.infer<typeof merchantsTabSchema>;

const clientTypeParam = z
  .enum(['all-vendor', 'business-vendor', 'vendor', 'employees', 'client'])
  .catch('all-vendor')
  .default('all-vendor');

const businessTypeParam = z
  .enum(['ALL', 'CORPORATE', 'NON-CORPORATE'])
  .catch('ALL')
  .default('ALL');

export const clientListSearchSchema = z.object({
  tab: merchantsTabSchema,
  /** Inner legacy segment (`user_statuses` in cpanel). */
  ctype: clientTypeParam,
  /** Corporate switch, only meaningful for `business-vendor`. */
  cbiz: businessTypeParam,
  csearch: z.coerce.string().optional().catch(undefined),
  /** Business status filter (`active` | `archive`), business-vendor only. */
  cstatus: z.enum(['active', 'archive']).optional().catch(undefined),
  /** Orders window for the with-orders aggregates (`YYYY-MM-DD`). */
  cfrom: z.coerce.string().optional().catch(undefined),
  ctill: z.coerce.string().optional().catch(undefined),
  ctariff: z.coerce.string().optional().catch(undefined),
  cmanager: z.coerce.string().optional().catch(undefined),
  /** Range filters (the legacy per-column FilterDialog, folded into one modal). */
  oc_from: z.coerce.string().optional().catch(undefined),
  oc_till: z.coerce.string().optional().catch(undefined),
  op_from: z.coerce.string().optional().catch(undefined),
  op_till: z.coerce.string().optional().catch(undefined),
  bal_from: z.coerce.string().optional().catch(undefined),
  bal_till: z.coerce.string().optional().catch(undefined),
  /** Single active sort, `"<field>:<1|2>"` (1 = ascending wire `true`). */
  csort: z.coerce.string().optional().catch(undefined),
  cpage: z.coerce.number().int().positive().catch(FIRST_PAGE).default(FIRST_PAGE),
  cpageSize: z.coerce
    .number()
    .int()
    .positive()
    .catch(DEFAULT_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  /** Deep link: opens the detail modal for this legacy row id. */
  client_id: z.coerce.string().optional().catch(undefined),
});
export type ClientListSearch = z.infer<typeof clientListSearchSchema>;
